import { on } from 'js/lib/events'

const DEFAULT_CONFIRMATION_MESSAGE = 'You have unsaved changes. Are you sure you want to leave this page?'
const dirtyFormStates = new Set()
let listeningForNavigation = false

// Returns a function that allows you to remove the alert programatically
export function protectFromUnsavedChanges(formInputs, options = {}) {
  const inputs = normalizeInputs(formInputs)
  const form = options.form || findForm(inputs)
  const formState = { message: options.message || DEFAULT_CONFIRMATION_MESSAGE }
  let destroyed = false
  const cleanupHandlers = inputs.flatMap(input => [
    on('change', input, onPageHasUnsavedChanges),
    on('input', input, onPageHasUnsavedChanges)
  ])

  if (form) cleanupHandlers.push(on('submit', form, onAllChangesSaved))

  onAllChangesSaved.destroy = destroy
  return onAllChangesSaved

  function onPageHasUnsavedChanges() {
    dirtyFormStates.add(formState)
    updateNavigationListeners()
  }

  function onAllChangesSaved() {
    dirtyFormStates.delete(formState)
    updateNavigationListeners()
  }

  function destroy() {
    if (destroyed) return

    destroyed = true
    onAllChangesSaved()
    cleanupHandlers.forEach(cleanupHandler => cleanupHandler())
  }
}

function normalizeInputs(formInputs) {
  if (typeof formInputs === 'string') return Array.from(document.querySelectorAll(formInputs))

  return Array.from(formInputs || [])
}

function findForm(inputs) {
  return inputs.find(input => input.form)?.form
}

function updateNavigationListeners() {
  if (dirtyFormStates.size > 0 && listeningForNavigation === false) {
    listeningForNavigation = true
    // use native event listeners to enable default browser behaviour
    window.addEventListener('beforeunload', beforeUnloadListener, { capture: true })
    document.addEventListener('click', navigationClickListener, { capture: true })
  }

  if (dirtyFormStates.size === 0 && listeningForNavigation === true) {
    listeningForNavigation = false
    window.removeEventListener('beforeunload', beforeUnloadListener, { capture: true })
    document.removeEventListener('click', navigationClickListener, { capture: true })
  }
}

function beforeUnloadListener(event) {
  event.preventDefault()
  const confirmationMessage = currentConfirmationMessage()

  return ((event || window.event).returnValue = confirmationMessage)
}

function navigationClickListener(event) {
  if (event.defaultPrevented || modifiedClick(event)) return

  const link = event.target.closest?.('a[href]')
  if (!link || !linkNavigatesAway(link)) return

  if (window.confirm(currentConfirmationMessage())) {
    dirtyFormStates.clear()
    updateNavigationListeners()
    return
  }

  event.preventDefault()
  event.stopImmediatePropagation()
}

function modifiedClick(event) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0
}

function linkNavigatesAway(link) {
  if (link.hasAttribute('download')) return false
  if (link.target && link.target !== '_self') return false

  const url = new URL(link.href, window.location.href)
  if (['mailto:', 'tel:', 'javascript:'].includes(url.protocol)) return false

  if (url.origin !== window.location.origin) return true
  if (url.pathname !== window.location.pathname || url.search !== window.location.search) return true

  return false
}

function currentConfirmationMessage() {
  return dirtyFormStates.values().next().value?.message || DEFAULT_CONFIRMATION_MESSAGE
}
